import React from 'react';
import { Bell, LogOut, Moon, Menu, Sun, X, User } from 'lucide-react';

const GlassTopBar = ({
  brandName = 'HaliLab',
  brandSubtitle = 'Customer Portal',
  userEmail = '',
  darkMode = false,
  toggleDarkMode = () => {},
  isSidebarCollapsed = false,
  onSidebarToggle = () => {},
  onLogout = () => {},
  primaryColor = '#6366f1' // Indigo for HaliLab
}) => {
  // Glass effect styles
  const glassStyle = {
    background: darkMode
      ? 'rgba(30, 27, 75, 0.85)'
      : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: darkMode
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 40,
    color: darkMode ? '#f3f4f6' : '#1f2937'
  };

  const buttonStyle = {
    padding: '0.5rem',
    borderRadius: '9999px',
    border: darkMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    background: darkMode
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    color: 'inherit'
  };

  const exitButtonStyle = {
    ...buttonStyle,
    borderRadius: '0.5rem',
    padding: '0.5rem 0.75rem',
    gap: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '500'
  };

  const userInfoStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '0.25rem',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    border: darkMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    background: darkMode
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'
  };

  const badgeStyle = {
    padding: '0.125rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.625rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    border: darkMode
      ? '1px solid rgba(99, 102, 241, 0.3)'
      : '1px solid rgba(99, 102, 241, 0.3)',
    background: darkMode
      ? 'rgba(99, 102, 241, 0.2)'
      : 'rgba(99, 102, 241, 0.1)',
    color: darkMode ? '#a5b4fc' : '#4f46e5'
  };

  return (
    <header style={glassStyle}>
      <div style={{ padding: '0 1.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '5rem'
        }}>
          {/* Left side - Menu toggle and brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={onSidebarToggle}
              style={buttonStyle}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <Menu size={24} /> : <X size={24} />}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '2rem' }}>🧪</span>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>
                  <span style={{ color: darkMode ? '#f3f4f6' : '#374151' }}>Hali</span>
                  <span style={{ color: primaryColor }}>Lab</span>
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: primaryColor,
                  opacity: 0.9
                }}>
                  {brandSubtitle}
                </div>
              </div>
            </div>
          </div>

          {/* Right side - User info and actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* User info box */}
            {userEmail && (
              <div style={userInfoStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <User size={14} />
                  <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                    {userEmail.split('@')[0]}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={badgeStyle}>Customer</span>
                </div>
                <div style={{
                  fontSize: '0.6875rem',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {userEmail}
                </div>
              </div>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              style={buttonStyle}
              title="Toggle theme"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Notifications */}
            <button style={buttonStyle} title="Notifications">
              <Bell size={18} />
            </button>

            {/* Exit button */}
            <button
              onClick={onLogout}
              style={exitButtonStyle}
            >
              <LogOut size={18} />
              <span>Exit</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default GlassTopBar;
